import { useEffect, useMemo, useState } from "react";
import { KeyRound } from "lucide-react";
import { useParams } from "react-router-dom";
import { api, applyTheme, STATUS, money } from "@/lib/api";
import { SiteHeader } from "@/components/Brand";
import { LeadCTA, LeadDialog } from "@/components/LeadForms";
import { SiteMap, PlotCard } from "@/components/SiteMap";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PRICES = [["any", "Any price"], ["500000", "Up to $500k"], ["600000", "Up to $600k"], ["750000", "Up to $750k"], ["1000000", "Up to $1M"]];

function Filters({ f, setF, bedsOpts }) {
  const sel = (k, opts, testid) => (
    <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
      <SelectTrigger className="w-full sm:w-44 h-11 rounded-full bg-white" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <div className="grid grid-cols-3 sm:flex gap-2 sm:gap-3">
      {sel("beds", [["any", "Any beds"], ...bedsOpts.map((b) => [String(b), `${b} beds`])], "filter-beds")}
      {sel("price", PRICES, "filter-price")}
      {sel("status", [["any", "All lots"], ["available", "Available"], ["reserved", "Reserved"], ["sold", "Sold"]], "filter-status")}
      <button onClick={() => setF({ ...f, ready: !f.ready })} className={`col-span-3 sm:col-span-1 h-11 px-5 rounded-full border text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${f.ready ? "bg-sky-500 border-sky-500 text-white" : "bg-white"}`} data-testid="filter-move-in-ready"><KeyRound className="w-4 h-4" />Move-in ready</button>
    </div>
  );
}

export default function DevelopmentPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ beds: "any", price: "any", status: "any", ready: false });
  const [sel, setSel] = useState(null);
  const [book, setBook] = useState(false);
  useEffect(() => {
    api.get(`/public/developments/${id}`).then((r) => { setData(r.data); applyTheme(r.data.builder); });
  }, [id]);
  const visible = useMemo(() => {
    if (!data) return new Set();
    return new Set(data.plots.filter((p) => (f.beds === "any" || p.beds === +f.beds) && (f.price === "any" || p.price <= +f.price) && (f.status === "any" || p.status === f.status) && (!f.ready || p.move_in_ready)).map((p) => p.id));
  }, [data, f]);
  if (!data) return <div className="min-h-screen" />;
  const { development: dev, plots, house_types, builder } = data;
  const houses = Object.fromEntries(house_types.map((h) => [h.id, h]));
  const bedsOpts = [...new Set(plots.map((p) => p.beds))].sort();
  const ctx = sel ? { development_id: dev.id, plot_id: sel.id, house_type_id: sel.house_type_id, label: `Lot ${sel.plot_number} · ${dev.name}` } : { development_id: dev.id, label: dev.name };

  return (
    <div className="min-h-screen pb-28">
      <SiteHeader builder={builder} right={<LeadCTA builderId={builder.id} context={{ development_id: dev.id, label: dev.name }} className="hidden md:flex" />} />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-ba">{dev.location}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold mt-3" data-testid="development-title">{dev.name}</h1>
        <p className="mt-4 max-w-2xl text-slate-600">{dev.description}</p>
      </section>
      <section className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <Filters f={f} setF={setF} bedsOpts={bedsOpts} />
          <div className="flex gap-4 text-xs text-slate-600" data-testid="status-legend">
            {Object.entries(STATUS).map(([k, s]) => <span key={k} className="flex items-center gap-1.5"><i className="w-3 h-3 rounded-full" style={{ background: s.pin }} />{s.label}</span>)}
            <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded-full bg-sky-500" />Move-in ready</span>
          </div>
        </div>
        <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
          {dev.site_plan_url ? <SiteMap dev={dev} plots={plots} visible={visible} selectedId={sel?.id} onSelect={setSel} /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="plot-list">
              {plots.filter((p) => visible.has(p.id)).map((p) => (
                <button key={p.id} onClick={() => setSel(p)} className={`text-left rounded-2xl bg-white border p-4 hover:shadow-md transition-shadow ${sel?.id === p.id ? "ring-2 ring-ba" : ""}`} data-testid={`plot-list-item-${p.plot_number}`}>
                  <span className="flex items-center justify-between"><span className="font-serif text-xl">Lot {p.plot_number}</span><i className="w-3 h-3 rounded-full" style={{ background: STATUS[p.status].pin }} /></span>
                  <span className="block text-sm text-slate-500 mt-1">{houses[p.house_type_id]?.name || ""} · {p.beds} bed</span>
                  <span className="block font-semibold mt-2">{p.status === "sold" ? "Sold" : money(p.price)}</span>
                </button>
              ))}
              {plots.length === 0 && <p className="text-slate-500 col-span-full">Lots coming soon — register your interest to hear first.</p>}
            </div>
          )}
          <div className="hidden lg:block sticky top-24">
            {sel ? <PlotCard plot={sel} house={houses[sel.house_type_id]} onClose={() => setSel(null)} onBook={() => setBook(true)} /> : (
              <div className="rounded-3xl border border-dashed p-8 text-slate-500 text-sm" data-testid="plot-card-empty">
                <p className="font-serif text-xl text-slate-900 mb-2">{visible.size} lots match</p>Tap a lot on the site plan to see photos, price and the 3D tour.
              </div>
            )}
          </div>
        </div>
      </section>
      {sel && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 p-3 max-h-[85vh] overflow-y-auto">
          <PlotCard plot={sel} house={houses[sel.house_type_id]} onClose={() => setSel(null)} onBook={() => setBook(true)} />
        </div>
      )}
      {!sel && <div className="md:hidden fixed bottom-0 inset-x-0 p-3 glass z-30"><LeadCTA builderId={builder.id} context={{ development_id: dev.id, label: dev.name }} className="justify-center" /></div>}
      <LeadDialog open={book} onOpenChange={setBook} kind="viewing" builderId={builder.id} context={ctx} />
    </div>
  );
}
